// "Materials - Illustrative Projects": ready-made SKFitter projects, each showing features of the fitter (orbitals and
// their symmetry, f orbitals, spin-orbit coupling, magnetism, starting-value sets, Berry curvature, AHC, SHC, VASP and
// Quantum ESPRESSO files). The list, and one project's guide with "Open" (a project of the user's own, set up).
import { Alert, Anchor, Badge, Button, Card, Group, Loader, Stack, Table, Text } from "@mantine/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router";
import { ErrorBox } from "../components/Errors";
import { PageTitle } from "../components/Layout";
import { Markdown } from "../components/Markdown";
import { useMe } from "../lib/hooks";
import { CODE_NAMES, openExample, useExample, useExamples, type Example } from "../lib/examples";
import { examplesPath } from "../lib/modules";
import { WannierExampleDetail, WannierExamplesTable } from "./WannierExamples";

export const EXAMPLES_TITLE = "Tight Binding - Illustrative Projects";

function useOpen() {
  const qc = useQueryClient();
  const nav = useNavigate();
  return useMutation({
    mutationFn: (id: string) => openExample(id),
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["examples"] });
      qc.invalidateQueries({ queryKey: ["me"] });
      nav(`/projects/${p.id}`);
    },
  });
}

export function Specialities({ items }: { items: string[] }) {
  return <Group gap={4}>{items.map((s) => <Badge key={s} variant="light" size="sm" style={{ textTransform: "none" }}>{s}</Badge>)}</Group>;
}

export function ExamplesPage() {
  const { data: all, error, isLoading } = useExamples();
  const data = all?.filter((e) => (e.product ?? "skfitter") === "skfitter");
  const nav = useNavigate();
  return (
    <>
      <PageTitle title={EXAMPLES_TITLE} />
      <Text size="sm" c="dimmed" mb="md" maw={900}>
        Ready-made Tight Binding projects: Slater-Koster fits, and below them Wannier functions. Each Slater-Koster one comes with its DFT files and a fit set-up that works, and
        shows particular features: orbitals and their symmetry (including f orbitals), spin-orbit coupling, magnetic
        materials, starting values from parameter sets, Berry curvature, anomalous and spin Hall conductivity, VASP and
        Quantum ESPRESSO input. Choose one to read its guide and open it as a project of your own; you then run each step
        (read the DFT data, fit, compute) with everything filled in.
      </Text>
      <ErrorBox error={error} />
      {isLoading && <Loader size="sm" />}
      {data && (
        <Card withBorder p={0}>
          <Table.ScrollContainer minWidth={700}>
            <Table highlightOnHover verticalSpacing="sm" data-testid="examples-table">
              <Table.Thead>
                <Table.Tr><Table.Th>#</Table.Th><Table.Th>Project</Table.Th><Table.Th>Material</Table.Th><Table.Th>Speciality</Table.Th>
                  <Table.Th>DFT files</Table.Th></Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.map((e, i) => (
                  <Table.Tr key={e.id} style={{ cursor: "pointer" }} onClick={() => nav(examplesPath("tight-binding", e.id))}>
                    <Table.Td>{i + 1}</Table.Td>
                    <Table.Td><Link to={examplesPath("tight-binding", e.id)} onClick={(ev) => ev.stopPropagation()}><Text fw={600} size="sm">{e.name}</Text></Link>
                      {e.project_id && <Badge size="xs" color="teal" variant="light" ml={6}>opened</Badge>}
                      {e.advanced && <Badge size="xs" color="grape" variant="light" ml={6}>Advanced resources</Badge>}</Table.Td>
                    <Table.Td><Text size="sm">{e.material}</Text></Table.Td>
                    <Table.Td><Specialities items={e.speciality} /></Table.Td>
                    <Table.Td><Text size="sm">{CODE_NAMES[e.code] ?? e.code}</Text></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>
      )}
      <WannierExamplesTable examples={all} />
    </>
  );
}

export function OpenExampleButton({ ex }: { ex: Example }) {
  const open = useOpen();
  const { data: me } = useMe();
  const allowed = !!me?.limits["skfitter.enabled"];
  return (
    <Stack gap={4} align="flex-start">
      <Button onClick={() => open.mutate(ex.id)} loading={open.isPending} disabled={!allowed}>
        {ex.project_id ? "Continue in my project" : "Open as my project"}
      </Button>
      {!allowed && <Text size="xs" c="orange">Tight Binding is not part of your plan.</Text>}
      <ErrorBox error={open.error} />
    </Stack>
  );
}

export function ExampleDetail() {
  const { id } = useParams();
  const { data: ex, error, isLoading } = useExample(id);
  const { data: me } = useMe();
  if (isLoading) return <Loader size="sm" />;
  if (error || !ex) return <ErrorBox error={error ?? new Error("Not found")} />;
  if (ex.product === "wannier") return <WannierExampleDetail ex={ex} />;
  return (
    <>
      <PageTitle title={ex.name}>
        <Button component={Link} to={examplesPath("tight-binding")} variant="default">All illustrative projects</Button>
      </PageTitle>
      <Stack gap="md" maw={1000}>
        <Card withBorder>
          <Stack gap={6}>
            <Text size="sm"><b>Material:</b> {ex.material} · <b>DFT files:</b> {CODE_NAMES[ex.code] ?? ex.code} ({ex.files.join(", ")})</Text>
            <Specialities items={ex.speciality} />
            <Text size="sm" c="dimmed">{ex.summary}</Text>
            <OpenExampleButton ex={ex} />
            {ex.advanced && <Alert color="teal" p="xs"><Text size="sm">This project runs with the Advanced plan's resources
              (property calculations, larger fits, longer jobs), granted by an administrator.</Text></Alert>}
            {ex.properties_continue && (
              <Text size="sm" data-testid="example-continues"><b>Continues in Properties:</b> {ex.properties_continue.what}
                {ex.properties_counterpart && <> - also ready-made as the Properties illustrative project{" "}
                  <Anchor component={Link} to={examplesPath("properties", ex.properties_counterpart)}>with this fit</Anchor></>}.</Text>
            )}
            {me && !me.limits["properties.geometric.enabled"] && !ex.advanced && (
              <Alert color="yellow" p="xs"><Text size="sm">The Wannier export of the last step and the Berry-phase properties in
                Properties need the Advanced plan; the DFT data and the fit work on every plan.</Text></Alert>
            )}
          </Stack>
        </Card>
        <Card withBorder data-testid="example-guide"><Markdown source={ex.guide ?? ""} /></Card>
      </Stack>
    </>
  );
}
